import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import OrderDetailModal from '../components/OrderDetailModal';
import './Orders.css';

const ORDER_DRAFT_KEY = 'sales_order_draft_mobile';

const defaultNewCustomer = {
  name: '',
  phone: '',
  address: '',
  type: 'retail'
};

const defaultFormData = {
  customer_id: '',
  order_type: 'wholesale',
  payment_method: 'cash',
  amount_paid: '',
  items: []
};

function canPreviewOrder(data) {
  return data.items.length > 0 &&
    data.items.every((item) => item.product_id && Number(item.quantity) > 0);
}

function canCreateOrder(data) {
  return Boolean(data.customer_id) && canPreviewOrder(data);
}

function buildOrderPayload(data, editingOrderId = null) {
  return {
    customer_id: data.customer_id ? parseInt(data.customer_id, 10) : null,
    order_type: data.order_type,
    payment_method: data.payment_method,
    amount_paid: data.payment_method === 'debt' && data.amount_paid !== ''
      ? parseInt(data.amount_paid, 10)
      : null,
    editing_order_id: editingOrderId,
    items: data.items.map((item) => ({
      product_id: parseInt(item.product_id, 10),
      quantity: parseInt(item.quantity, 10)
    }))
  };
}

function getDraft() {
  try {
    const rawDraft = localStorage.getItem(ORDER_DRAFT_KEY);
    return rawDraft ? JSON.parse(rawDraft) : null;
  } catch (error) {
    return null;
  }
}

function saveDraft(draft) {
  localStorage.setItem(ORDER_DRAFT_KEY, JSON.stringify(draft));
}

function clearDraft() {
  localStorage.removeItem(ORDER_DRAFT_KEY);
}

function formatCurrency(value) {
  return `${Number(value || 0).toLocaleString('vi-VN')} ₫`;
}

function paymentMethodLabel(method) {
  return method === 'debt' ? 'Công nợ' : 'Thanh toán ngay';
}

function paymentStatusLabel(status) {
  if (status === 'paid') {
    return 'Đã thu';
  }

  if (status === 'partial') {
    return 'Thu một phần';
  }

  return 'Chưa thu';
}

function inferPaymentStatus(paymentMethod, totalAmount, amountPaid) {
  if (paymentMethod === 'cash') {
    return 'paid';
  }

  const normalizedAmountPaid = Number(amountPaid) || 0;
  if (normalizedAmountPaid <= 0) {
    return 'unpaid';
  }

  if (normalizedAmountPaid >= totalAmount) {
    return 'paid';
  }

  return 'partial';
}

function buildFormDataFromOrder(order) {
  return {
    customer_id: order.customer_id ? String(order.customer_id) : '',
    order_type: order.order_type || 'wholesale',
    payment_method: order.payment_method || 'cash',
    amount_paid: order.payment_method === 'debt'
      ? String(order.amount_paid ?? 0)
      : '',
    items: (order.details || []).map((item) => ({
      product_id: String(item.product_id),
      quantity: Number(item.quantity) || 1
    }))
  };
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [newCustomer, setNewCustomer] = useState(defaultNewCustomer);
  const [formData, setFormData] = useState(defaultFormData);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get('/api/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  const fetchOrderPreview = useCallback(async (data, currentEditingOrderId = null) => {
    try {
      setPreviewLoading(true);
      const response = await axios.post('/api/orders/preview', buildOrderPayload(data, currentEditingOrderId));
      setPreview(response.data);
    } catch (error) {
      console.error('Error previewing order:', error);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
    fetchProducts();
  }, [fetchOrders, fetchCustomers, fetchProducts]);

  useEffect(() => {
    if (!showForm || !canPreviewOrder(formData)) {
      setPreview(null);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      fetchOrderPreview(formData, editingOrderId);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [showForm, formData, editingOrderId, fetchOrderPreview]);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => String(customer.id) === String(formData.customer_id)) || null,
    [customers, formData.customer_id]
  );

  const selectedItems = useMemo(() => {
    return formData.items
      .map((item) => {
        const product = products.find((candidate) => candidate.id === parseInt(item.product_id, 10));
        if (!product) {
          return null;
        }

        const previewItem = preview?.items?.find(
          (candidate) => candidate.product_id === parseInt(item.product_id, 10)
        );

        const fallbackUnitPrice = product.retail_price_70k;
        const unitPrice = previewItem?.unit_price ?? fallbackUnitPrice;
        const quantity = Number(item.quantity) || 0;

        return {
          ...item,
          product,
          quantity,
          pricingReferenceQuantity: previewItem?.pricing_reference_quantity ?? quantity,
          unitPrice,
          totalPrice: previewItem?.total_price ?? (unitPrice * quantity)
        };
      })
      .filter(Boolean);
  }, [formData.items, preview, products]);

  const searchResults = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) {
      return [];
    }

    const selectedIds = new Set(formData.items.map((item) => parseInt(item.product_id, 10)));

    return products
      .filter((product) => !selectedIds.has(product.id))
      .filter((product) => {
        const productName = product.name.toLowerCase();
        return productName.includes(keyword) || String(product.id).includes(keyword);
      })
      .slice(0, 8);
  }, [formData.items, products, searchTerm]);

  const totalQuantity = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = preview?.total_amount ?? selectedItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const amountPaid = preview?.amount_paid ?? (
    formData.payment_method === 'cash'
      ? totalAmount
      : Math.max(0, Number(formData.amount_paid) || 0)
  );
  const debtAmount = preview?.debt_amount ?? Math.max(0, totalAmount - amountPaid);
  const paymentStatus = preview?.payment_status ?? inferPaymentStatus(formData.payment_method, totalAmount, amountPaid);
  const customerAccumulatedQuantity = Number(
    preview?.customer_accumulated_quantity
      ?? selectedCustomer?.total_purchased_quantity
      ?? 0
  );

  const resetForm = () => {
    setEditingOrderId(null);
    setFormData(defaultFormData);
    setPreview(null);
    setSearchTerm('');
    setShowNewCustomerForm(false);
    setNewCustomer(defaultNewCustomer);
  };

  const openOrderForm = () => {
    setEditingOrderId(null);
    const draft = getDraft();
    if (draft?.formData) {
      setFormData({
        ...defaultFormData,
        ...draft.formData
      });
      setNewCustomer(draft.newCustomer || defaultNewCustomer);
      setShowNewCustomerForm(Boolean(draft.showNewCustomerForm));
    } else {
      resetForm();
    }

    setSearchTerm('');
    setShowForm(true);
  };

  const closeOrderForm = () => {
    setShowForm(false);
    setSearchTerm('');
    resetForm();
  };

  const handleSelectProduct = (product) => {
    setFormData((prev) => {
      const existingItem = prev.items.find((item) => parseInt(item.product_id, 10) === product.id);

      if (existingItem) {
        return {
          ...prev,
          items: prev.items.map((item) => (
            parseInt(item.product_id, 10) === product.id
              ? { ...item, quantity: (Number(item.quantity) || 0) + 1 }
              : item
          ))
        };
      }

      return {
        ...prev,
        items: [...prev.items, { product_id: product.id.toString(), quantity: 1 }]
      };
    });

    setSearchTerm('');
  };

  const handleQuickAddFirstResult = () => {
    if (searchResults.length > 0) {
      handleSelectProduct(searchResults[0]);
    }
  };

  const handleQuantityAdjust = (productId, delta) => {
    setFormData((prev) => {
      const updatedItems = prev.items
        .map((item) => {
          if (parseInt(item.product_id, 10) !== productId) {
            return item;
          }

          return {
            ...item,
            quantity: Math.max(0, (Number(item.quantity) || 0) + delta)
          };
        })
        .filter((item) => Number(item.quantity) > 0);

      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  const handleQuantityInputChange = (productId, value) => {
    if (!/^\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => (
        parseInt(item.product_id, 10) === productId
          ? { ...item, quantity: value === '' ? '' : parseInt(value, 10) }
          : item
      ))
    }));
  };

  const handleQuantityInputBlur = (productId) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items
        .map((item) => {
          if (parseInt(item.product_id, 10) !== productId) {
            return item;
          }

          return {
            ...item,
            quantity: Math.max(1, Number(item.quantity) || 1)
          };
        })
        .filter((item) => Number(item.quantity) > 0)
    }));
  };

  const handleCustomerChange = (e) => {
    setFormData((prev) => ({ ...prev, customer_id: e.target.value }));
  };

  const handleOrderTypeChange = (orderType) => {
    setFormData((prev) => ({ ...prev, order_type: orderType }));
    setNewCustomer((prev) => ({
      ...prev,
      type: orderType === 'wholesale' ? 'wholesale' : prev.type
    }));
  };

  const handlePaymentMethodChange = (paymentMethod) => {
    setFormData((prev) => ({
      ...prev,
      payment_method: paymentMethod
    }));
  };

  const handleAmountPaidChange = (e) => {
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        amount_paid: value
      }));
    }
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomer = async () => {
    try {
      setCustomerSaving(true);
      const response = await axios.post('/api/customers', newCustomer);
      const createdCustomer = response.data;
      setCustomers((prev) => [createdCustomer, ...prev]);
      setFormData((prev) => ({ ...prev, customer_id: createdCustomer.id.toString() }));
      setShowNewCustomerForm(false);
      setNewCustomer({ ...defaultNewCustomer, type: formData.order_type === 'wholesale' ? 'wholesale' : 'retail' });
    } catch (error) {
      console.error('Error creating customer:', error);
      alert(error.response?.data?.error || 'Lỗi khi thêm khách hàng');
    } finally {
      setCustomerSaving(false);
    }
  };

  const handleSaveDraft = () => {
    saveDraft({
      formData,
      newCustomer,
      showNewCustomerForm
    });
    alert('Đã lưu tạm đơn hàng');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canCreateOrder(formData)) {
      alert('Vui lòng chọn khách hàng và thêm ít nhất một sản phẩm');
      return;
    }

    try {
      if (editingOrderId) {
        await axios.put(`/api/orders/${editingOrderId}`, buildOrderPayload(formData, editingOrderId));
      } else {
        await axios.post('/api/orders', buildOrderPayload(formData));
      }
      clearDraft();
      closeOrderForm();
      fetchOrders();
      fetchProducts();
    } catch (error) {
      console.error('Error saving order:', error);
      alert(error.response?.data?.error || (editingOrderId ? 'Lỗi khi cập nhật hóa đơn' : 'Lỗi khi đặt hàng'));
    }
  };

  const viewOrder = async (id) => {
    try {
      const response = await axios.get(`/api/orders/${id}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Error fetching order detail:', error);
    }
  };

  const openEditOrder = async (id) => {
    try {
      const response = await axios.get(`/api/orders/${id}`);
      const order = response.data;
      setSelectedOrder(null);
      setEditingOrderId(order.id);
      setFormData(buildFormDataFromOrder(order));
      setPreview(null);
      setSearchTerm('');
      setShowNewCustomerForm(false);
      setNewCustomer(defaultNewCustomer);
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching order for edit:', error);
      alert(error.response?.data?.error || 'Không thể tải dữ liệu hóa đơn để sửa');
    }
  };

  const handleDeleteOrder = async (id) => {
    const confirmed = window.confirm(`Bạn có chắc muốn xóa hóa đơn #${id}? Tồn kho sẽ được hoàn lại.`);
    if (!confirmed) {
      return;
    }

    try {
      setDeletingOrderId(id);
      await axios.delete(`/api/orders/${id}`);
      setOrders((prev) => prev.filter((order) => order.id !== id));
      if (selectedOrder?.id === id) {
        setSelectedOrder(null);
      }
      fetchProducts();
    } catch (error) {
      console.error('Error deleting order:', error);
      alert(error.response?.data?.error || 'Không thể xóa hóa đơn');
    } finally {
      setDeletingOrderId(null);
    }
  };

  const handleOrderPaymentStatusChange = async (order, nextStatus) => {
    try {
      setUpdatingOrderId(order.id);

      let paymentMethod = order.payment_method === 'debt' ? 'debt' : 'cash';
      let amountPaidValue = null;

      if (nextStatus === 'paid') {
        amountPaidValue = Number(order.total_amount) || 0;
      } else if (nextStatus === 'unpaid') {
        paymentMethod = 'debt';
        amountPaidValue = 0;
      } else {
        paymentMethod = 'debt';
        const inputValue = window.prompt(
          'Nhập số tiền đã thu cho hóa đơn này',
          String(order.amount_paid ?? 0)
        );

        if (inputValue === null) {
          return;
        }

        const normalizedInput = inputValue.replace(/[^\d]/g, '');
        amountPaidValue = Number(normalizedInput);

        if (!Number.isFinite(amountPaidValue) || amountPaidValue <= 0 || amountPaidValue >= Number(order.total_amount)) {
          alert('Số tiền đã thu phải lớn hơn 0 và nhỏ hơn tổng đơn hàng để ghi nhận thu một phần');
          return;
        }
      }

      const response = await axios.put(`/api/orders/${order.id}/payment`, {
        payment_method: paymentMethod,
        amount_paid: amountPaidValue
      });

      const updatedOrder = response.data;
      setOrders((prev) => prev.map((item) => (item.id === updatedOrder.id ? updatedOrder : item)));
      if (selectedOrder?.id === updatedOrder.id) {
        setSelectedOrder(updatedOrder);
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert(error.response?.data?.error || 'Không thể cập nhật trạng thái thu tiền');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  if (loading) {
    return <div className="loading">Đang tải hóa đơn...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2>Quản Lý Hóa Đơn</h2>
          <p>Giao diện đặt hàng nhanh theo kiểu mobile, tự tính giá và tổng tiền.</p>
        </div>
        <button className="success" onClick={openOrderForm}>+ Tạo hóa đơn</button>
      </div>

      {showForm && (
        <div className="modal show order-modal-overlay">
          <div className="modal-content order-modal-content">
            <div className="order-mobile-header">
              <button type="button" className="order-header-close" onClick={closeOrderForm}>×</button>
              <div>
                <h3 className="modal-title" style={{ margin: 0 }}>
                  {editingOrderId ? `Sửa hóa đơn #${editingOrderId}` : 'Đặt hàng'}
                </h3>
                <div className="order-header-subtitle">
                  {editingOrderId
                    ? 'Chỉnh sửa khách hàng, sản phẩm, thanh toán và công nợ của hóa đơn'
                    : 'Tạo hóa đơn nhanh theo sản phẩm và số lượng'}
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="order-mobile-body">
              <div className="order-search-row">
                <div className="order-search-input-wrapper">
                  <span className="order-search-icon">⌕</span>
                  <input
                    className="order-search-input"
                    type="text"
                    placeholder="Tên, mã hàng..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="order-square-btn"
                  onClick={handleQuickAddFirstResult}
                  disabled={searchResults.length === 0}
                  title="Thêm nhanh sản phẩm đầu tiên"
                >
                  +
                </button>
                <button
                  type="button"
                  className="order-square-btn"
                  onClick={() => setSearchTerm('')}
                  title="Xóa tìm kiếm"
                >
                  ⌫
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="order-card order-search-results">
                  {searchResults.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className="order-search-result-item"
                      onClick={() => handleSelectProduct(product)}
                    >
                      <div>
                        <div className="order-search-result-name">{product.name}</div>
                        <div className="order-search-result-meta">Còn {product.quantity || 0} • {product.retail_price_70k.toLocaleString()} ₫</div>
                      </div>
                      <span className="order-search-result-add">Thêm</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="order-card order-customer-card">
                <div className="order-meta-row">
                  <div>
                    <div className="order-meta-label">Khách hàng</div>
                    <div className="order-meta-value">{selectedCustomer ? selectedCustomer.name : 'Chưa chọn khách hàng'}</div>
                  </div>
                  <button type="button" className="order-link-btn" onClick={() => setShowNewCustomerForm((prev) => !prev)}>
                    + Khách mới
                  </button>
                </div>

                <select className="order-customer-select" value={formData.customer_id} onChange={handleCustomerChange}>
                  <option value="">-- Chọn khách hàng --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>

                {selectedCustomer && (
                  <div className="order-customer-tier">
                    Tổng mua tích lũy: <strong>{customerAccumulatedQuantity.toLocaleString('vi-VN')}</strong> sản phẩm
                  </div>
                )}

                {showNewCustomerForm && (
                  <div className="order-inline-form">
                    <input type="text" name="name" placeholder="Tên khách hàng" value={newCustomer.name} onChange={handleNewCustomerChange} />
                    <input type="tel" name="phone" placeholder="Số điện thoại" value={newCustomer.phone} onChange={handleNewCustomerChange} />
                    <input type="text" name="address" placeholder="Địa chỉ" value={newCustomer.address} onChange={handleNewCustomerChange} />
                    <select name="type" value={newCustomer.type} onChange={handleNewCustomerChange}>
                      <option value="retail">Bán lẻ</option>
                      <option value="wholesale">Bán sỉ</option>
                    </select>
                    <button type="button" onClick={handleCreateCustomer} disabled={customerSaving || !newCustomer.name.trim()}>
                      {customerSaving ? 'Đang thêm...' : 'Lưu khách hàng'}
                    </button>
                  </div>
                )}
              </div>

              <div className="order-card order-sale-type-card">
                <div className="order-meta-label">Loại bán</div>
                <div className="order-type-toggle">
                  <button
                    type="button"
                    className={`order-type-btn ${formData.order_type === 'retail' ? 'active' : ''}`}
                    onClick={() => handleOrderTypeChange('retail')}
                  >
                    Bán lẻ
                  </button>
                  <button
                    type="button"
                    className={`order-type-btn ${formData.order_type === 'wholesale' ? 'active' : ''}`}
                    onClick={() => handleOrderTypeChange('wholesale')}
                  >
                    Bán sỉ
                  </button>
                </div>
                {formData.order_type === 'wholesale' && (
                  <div className="order-sale-tier-note">
                    Giá sỉ được áp theo bậc lớn hơn giữa số lượng từng sản phẩm và tổng mua tích lũy của khách hàng hiện tại là <strong>{customerAccumulatedQuantity.toLocaleString('vi-VN')}</strong> sản phẩm.
                  </div>
                )}
              </div>

              <div className="order-card order-payment-card">
                <div className="order-meta-label">Thanh toán</div>
                <div className="order-type-toggle">
                  <button
                    type="button"
                    className={`order-type-btn ${formData.payment_method === 'cash' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('cash')}
                  >
                    Thanh toán ngay
                  </button>
                  <button
                    type="button"
                    className={`order-type-btn ${formData.payment_method === 'debt' ? 'active' : ''}`}
                    onClick={() => handlePaymentMethodChange('debt')}
                  >
                    Công nợ
                  </button>
                </div>

                {formData.payment_method === 'debt' && (
                  <div className="order-payment-form">
                    <div className="order-meta-label" style={{ marginBottom: '6px' }}>Đã thu trước</div>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Nhập số tiền đã thu trước nếu có"
                      value={formData.amount_paid}
                      onChange={handleAmountPaidChange}
                    />
                    <div className="order-payment-hint">Bỏ trống hoặc nhập `0` nếu khách chưa thanh toán, hệ thống sẽ tự ghi nhận công nợ còn lại.</div>
                  </div>
                )}
              </div>

              <div className="order-selected-list order-selected-panel">
                {selectedItems.length === 0 ? (
                  <div className="order-card order-empty-order">
                    Tìm kiếm sản phẩm ở trên rồi bấm `+` hoặc chọn trực tiếp từ danh sách gợi ý.
                  </div>
                ) : (
                  selectedItems.map((item) => (
                    <div key={item.product.id} className="order-item-card">
                      <div className="order-item-image">🖼️</div>
                      <div className="order-item-info">
                        <div className="order-item-name">{item.product.name}</div>
                        <div className="order-item-price">
                          {previewLoading ? 'Đang tính...' : `${item.unitPrice.toLocaleString()} ₫`}
                        </div>
                        {formData.order_type === 'wholesale' && (
                          <div className="order-item-tier">
                            Áp giá bậc {Number(item.pricingReferenceQuantity || item.quantity).toLocaleString('vi-VN')} sản phẩm
                          </div>
                        )}
                      </div>
                      <div className="order-qty-stepper">
                        <button type="button" onClick={() => handleQuantityAdjust(item.product.id, -1)}>−</button>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={item.quantity}
                          onChange={(e) => handleQuantityInputChange(item.product.id, e.target.value)}
                          onBlur={() => handleQuantityInputBlur(item.product.id)}
                        />
                        <button type="button" onClick={() => handleQuantityAdjust(item.product.id, 1)}>+</button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="order-footer order-summary-panel">
                <div className="order-footer-summary">
                  <div>
                    Tổng tiền hàng
                    <span className="order-footer-count">{totalQuantity}</span>
                  </div>
                  <div>{previewLoading ? 'Đang tính...' : formatCurrency(totalAmount)}</div>
                </div>
                <div className="order-payment-summary">
                  <div className="order-payment-row">
                    <span>Hình thức</span>
                    <strong>{paymentMethodLabel(formData.payment_method)}</strong>
                  </div>
                  <div className="order-payment-row">
                    <span>Trạng thái thu</span>
                    <strong className={`order-status-chip ${paymentStatus}`}>{paymentStatusLabel(paymentStatus)}</strong>
                  </div>
                  <div className="order-payment-row">
                    <span>Đã thu</span>
                    <strong>{formatCurrency(amountPaid)}</strong>
                  </div>
                  <div className="order-payment-row">
                    <span>Công nợ còn lại</span>
                    <strong>{formatCurrency(debtAmount)}</strong>
                  </div>
                </div>
                {!formData.customer_id && (
                  <div className="order-footer-note">Bạn có thể xem trước giá, nhưng cần chọn hoặc thêm khách hàng để đặt hàng.</div>
                )}
                <div className="order-footer-actions">
                  <button
                    type="button"
                    className="order-outline-btn"
                    onClick={editingOrderId ? closeOrderForm : handleSaveDraft}
                  >
                    {editingOrderId ? 'Hủy sửa' : 'Lưu tạm'}
                  </button>
                  <button type="submit" className="order-primary-btn" disabled={!canCreateOrder(formData)}>
                    {editingOrderId ? 'Cập nhật hóa đơn' : 'Đặt hàng'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}

      {orders.length === 0 ? (
        <div className="empty-state">Chưa có hóa đơn nào</div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Khách hàng</th>
                <th>Loại</th>
                <th>Tổng tiền</th>
                <th>Thanh toán</th>
                <th>Đã thu / Còn nợ</th>
                <th>Trạng thái thu</th>
                <th>Ngày</th>
                <th>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>{order.customer_name}</td>
                  <td>{order.order_type === 'wholesale' ? 'Bán sỉ' : 'Bán lẻ'}</td>
                  <td>{formatCurrency(order.total_amount)}</td>
                  <td>{paymentMethodLabel(order.payment_method)}</td>
                  <td>
                    <div>Đã thu: {formatCurrency(order.amount_paid)}</div>
                    <div style={{ color: (Number(order.debt_amount) || 0) > 0 ? '#dc3545' : '#218838', marginTop: '4px' }}>
                      Còn nợ: {formatCurrency(order.debt_amount)}
                    </div>
                  </td>
                  <td>
                    <select
                      value={order.payment_status || 'paid'}
                      onChange={(e) => handleOrderPaymentStatusChange(order, e.target.value)}
                      disabled={updatingOrderId === order.id}
                    >
                      <option value="paid">Đã thu</option>
                      <option value="unpaid">Chưa thu</option>
                      <option value="partial">Thu một phần</option>
                    </select>
                  </td>
                  <td>{new Date(order.created_at).toLocaleDateString('vi-VN')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={() => viewOrder(order.id)}>Xem</button>
                      <button onClick={() => openEditOrder(order.id)}>Sửa</button>
                      <button
                        className="danger"
                        onClick={() => handleDeleteOrder(order.id)}
                        disabled={deletingOrderId === order.id}
                      >
                        {deletingOrderId === order.id ? 'Đang xóa...' : 'Xóa'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Orders;
